import { useState } from "react"
import {
  FlaskConical,
  Play,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  User,
  Users,
  Ruler,
  CalendarRange,
  Target,
  Dumbbell,
  NotebookPen,
  Weight,
  FileText,
  HeartPulse,
} from "lucide-react"
import { toast } from "sonner"
import {
  runPlaygroundGeneration,
  getUserProfile,
  type PlaygroundProfile,
  type PlaygroundGoal,
  type PlaygroundResult,
} from "@/lib/api"
import { UserCombobox, type UserOption } from "@/components/user-combobox"
import {
  getDaySessions,
  getDayLabel,
  isRestDay,
  walkSeasonWeeks,
  type DayData,
} from "@/types/workout"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { NutritionCoachPanel } from "@/components/NutritionCoachPanel"
import { PlaygroundDayFeedback } from "@/components/PlaygroundDayFeedback"
import { PlaygroundWeekFeedback } from "@/components/PlaygroundWeekFeedback"
import { ARCHETYPE_LABEL, labelForArchetype } from "@/components/ArchetypeRow"

// Names must match backend canonical aliases (program_sanity_service._ALIAS_TO_MODALITY);
// anything the backend doesn't recognize is silently dropped from the plan.
// Sports live in SPORT_OPTIONS → sport_focus, not selected_activities.
const ACTIVITY_OPTIONS = [
  "Weightlifting",
  "Yoga",
  "Pilates",
  "Run",
  "Swim",
  "Cycling",
  "Hike",
  "Walk",
  "HIIT",
  "Rowing",
] as const

// Non-empty sport_focus triggers Sport-Specific archetype (archetype_service._rule_1_sport_focus).
const SPORT_OPTIONS = [
  "Basketball",
  "Soccer",
  "Tennis",
  "Pickleball",
  "Volleyball",
  "Hockey",
  "Baseball",
  "Golf",
  "MMA",
  "Climbing",
  "Surfing",
  "Skiing",
  "Snowboarding",
] as const

const GENDER_OPTIONS = ["Female", "Male", "Non-binary", "Prefer not to say"] as const

const DAY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const

const EQUIPMENT_OPTIONS = [
  "Dumbbells",
  "Barbell",
  "Cable machine",
  "Kettlebells",
  "Resistance bands",
  "Pull-up bar",
  "Bodyweight only",
  "Treadmill",
  "Bike",
  "Pool",
  "Full gym",
] as const

// Goal `type` slugs must match mobile onboarding / backend expectations.
const GOAL_PRIMARY = [
  { type: "gain-muscle", name: "Build muscle" },
  { type: "lose-body-fat", name: "Lose fat" },
  { type: "get-stronger", name: "Get stronger" },
  { type: "get-faster", name: "Get faster" },
  { type: "train-event", name: "Train for an event" },
  { type: "push-myself", name: "Push myself" },
] as const

const MUSCLE_GROUPS = [
  "Chest",
  "Back",
  "Shoulders",
  "Arms",
  "Legs",
  "Glutes",
  "Core",
] as const

const PACE_UNITS = ["min/mile", "min/km"] as const

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const

interface GoalDraft {
  type: string
  name: string
  details: string
  muscleGroups: string[]
  currentMuscleMass: string
  targetMuscleMass: string
  currentBodyFat: string
  targetBodyFat: string
  paceMinutes: string
  paceSeconds: string
  paceUnit: (typeof PACE_UNITS)[number]
  eventType: string
  eventMonth: string
  eventYear: string
  trainingStatus: string
  currentStatus: string
  pushType: string
}

const DEFAULT_GOAL = (type: string, name: string): GoalDraft => ({
  type,
  name,
  details: "",
  muscleGroups: [],
  currentMuscleMass: "",
  targetMuscleMass: "",
  currentBodyFat: "",
  targetBodyFat: "",
  paceMinutes: "",
  paceSeconds: "",
  paceUnit: "min/mile",
  eventType: "",
  eventMonth: "",
  eventYear: "",
  trainingStatus: "",
  currentStatus: "",
  pushType: "",
})

/** Per-activity availability — mirrors mobile training_schedule + preferred_days. */
interface ActivityPlan {
  sessionsPerWeek: string
  preferredDays: string[]
}

interface FormState {
  firstName: string
  lastName: string
  age: string
  gender: string
  heightFeet: string
  heightInches: string
  weightLbs: string
  location: string
  activities: string[]
  /** Written to sport_focus; triggers Sport-Specific archetype when non-empty. */
  sportFocus: string[]
  activityPlans: Record<string, ActivityPlan>
  goals: GoalDraft[]
  equipment: string[]
  oneRepMaxBench: string
  oneRepMaxSquat: string
  oneRepMaxDeadlift: string
  /** How many consecutive weeks the agent should generate (1-4). Lets
   *  admins review progressive overload across a phase block. */
  weeksToGenerate: number
  /** Free-text case study / narrative the admin writes describing the user
   *  the model should generate for. Prepended to profile_summary so it
   *  reaches the agent as primary context. Independent of importing a real
   *  user — use it to sketch a scenario from scratch or to layer extra
   *  narrative on top of an imported profile. */
  caseStudy: string
  profileSummary: string
  /** Optional full system-prompt override. When non-empty, REPLACES the
   *  default test-generation system prompt (principles + exercise library
   *  + weight-anchoring block all bypassed). Use to A/B test new prompts
   *  without redeploying. */
  systemPrompt: string
  /** Force a specific archetype for this run. "" means let the classifier decide. */
  archetypeOverride: string
  /** Synthetic wearable / readiness simulation. When off, the run behaves like
   *  a no-wearable user (MC V defaults to GREEN). Each level is "" (unset) |
   *  "green" | "yellow" | "red"; the backend runs the REAL readiness scorer. */
  wearableEnabled: boolean
  hrvLevel: string
  rhrLevel: string
  sleepLevel: string
  subjectiveLevel: string
}

const DEFAULTS: FormState = {
  firstName: "Test",
  lastName: "User",
  age: "30",
  gender: "Female",
  heightFeet: "5",
  heightInches: "6",
  weightLbs: "140",
  location: "San Francisco, CA",
  activities: ["Weightlifting", "Run"],
  sportFocus: [],
  activityPlans: {
    Weightlifting: {
      sessionsPerWeek: "3",
      preferredDays: ["Monday", "Wednesday", "Friday"],
    },
    Run: {
      sessionsPerWeek: "2",
      preferredDays: ["Tuesday", "Saturday"],
    },
  },
  goals: [DEFAULT_GOAL("gain-muscle", "Build muscle")],
  equipment: ["Dumbbells", "Full gym"],
  oneRepMaxBench: "",
  oneRepMaxSquat: "",
  oneRepMaxDeadlift: "",
  weeksToGenerate: 1,
  caseStudy: "",
  profileSummary: "",
  systemPrompt: "",
  archetypeOverride: "",
  wearableEnabled: false,
  hrvLevel: "",
  rhrLevel: "",
  sleepLevel: "",
  subjectiveLevel: "",
}

function Toggle({
  options,
  selected,
  onChange,
  ariaLabel,
}: {
  options: readonly string[]
  selected: string[]
  onChange: (next: string[]) => void
  ariaLabel: string
}) {
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-label={ariaLabel}>
      {options.map((opt) => {
        const isOn = selected.includes(opt)
        return (
          <button
            key={opt}
            type="button"
            onClick={() => {
              onChange(
                isOn ? selected.filter((s) => s !== opt) : [...selected, opt],
              )
            }}
            className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
              isOn
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background hover:bg-accent"
            }`}
          >
            {opt}
          </button>
        )
      })}
    </div>
  )
}

/** Coarse GREEN/YELLOW/RED picker for one readiness metric. "" = unset. */
function LevelRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string
  hint?: string
  value: string
  onChange: (next: string) => void
}) {
  const OPTS: { v: string; label: string; cls: string }[] = [
    { v: "", label: "—", cls: "" },
    { v: "green", label: "Green", cls: "bg-emerald-500 text-white border-emerald-500" },
    { v: "yellow", label: "Yellow", cls: "bg-amber-400 text-black border-amber-400" },
    { v: "red", label: "Red", cls: "bg-red-500 text-white border-red-500" },
  ]
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <span className="text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      </div>
      <div className="flex gap-1.5" role="group" aria-label={label}>
        {OPTS.map((o) => {
          const on = value === o.v
          return (
            <button
              key={o.v || "none"}
              type="button"
              onClick={() => onChange(o.v)}
              className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
                on ? o.cls || "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent"
              }`}
            >
              {o.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function FieldLabel({
  children,
  hint,
}: {
  children: React.ReactNode
  hint?: string
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium">{children}</span>
      {hint && (
        <span className="block text-xs text-muted-foreground">{hint}</span>
      )}
    </label>
  )
}

function Section({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-lg border bg-card p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-muted p-2">
          <Icon className="size-4 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-base font-semibold leading-tight">{title}</h3>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {description}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  )
}

function GoalEditor({
  goal,
  index,
  onUpdate,
  onRemove,
  canRemove,
}: {
  goal: GoalDraft
  index: number
  onUpdate: (next: GoalDraft) => void
  onRemove: () => void
  canRemove: boolean
}) {
  const set = <K extends keyof GoalDraft>(key: K, value: GoalDraft[K]) => {
    onUpdate({ ...goal, [key]: value })
  }

  const onTypeChange = (newType: string) => {
    const meta = GOAL_PRIMARY.find((g) => g.type === newType)
    if (!meta) return
    onUpdate({ ...DEFAULT_GOAL(meta.type, meta.name), details: goal.details })
  }

  return (
    <div className="rounded-md border bg-background p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <FieldLabel hint="What is the user training toward?">
            Goal {index + 1}
          </FieldLabel>
          <select
            value={goal.type}
            onChange={(e) => onTypeChange(e.target.value)}
            className="w-full mt-1 px-3 py-2 border rounded-md bg-background text-sm"
          >
            {GOAL_PRIMARY.map((g) => (
              <option key={g.type} value={g.type}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            title="Remove goal"
            className="mt-5 p-2 rounded-md hover:bg-destructive/10 text-destructive"
          >
            <Trash2 className="size-4" />
          </button>
        )}
      </div>

      {/* Type-specific sub-fields — mirrors the goal-detail screens in the mobile flow. */}
      {(goal.type === "gain-muscle" || goal.type === "get-stronger") && (
        <div>
          <FieldLabel hint="Which muscle groups should we focus on?">
            Muscle groups
          </FieldLabel>
          <Toggle
            options={MUSCLE_GROUPS}
            selected={goal.muscleGroups}
            onChange={(next) => set("muscleGroups", next)}
            ariaLabel="Muscle groups"
          />
        </div>
      )}

      {goal.type === "gain-muscle" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FieldLabel hint="Optional, lbs">Current muscle mass</FieldLabel>
            <input
              type="number"
              value={goal.currentMuscleMass}
              onChange={(e) => set("currentMuscleMass", e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
          <div>
            <FieldLabel hint="Optional, lbs">Target muscle mass</FieldLabel>
            <input
              type="number"
              value={goal.targetMuscleMass}
              onChange={(e) => set("targetMuscleMass", e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
        </div>
      )}

      {goal.type === "lose-body-fat" && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <FieldLabel hint="Optional, %">Current body fat</FieldLabel>
            <input
              type="number"
              value={goal.currentBodyFat}
              onChange={(e) => set("currentBodyFat", e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
          <div>
            <FieldLabel hint="Optional, %">Target body fat</FieldLabel>
            <input
              type="number"
              value={goal.targetBodyFat}
              onChange={(e) => set("targetBodyFat", e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
        </div>
      )}

      {goal.type === "get-faster" && (
        <div>
          <FieldLabel hint="What pace are they aiming for?">
            Target pace
          </FieldLabel>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              max={59}
              value={goal.paceMinutes}
              onChange={(e) => set("paceMinutes", e.target.value)}
              placeholder="min"
              className="w-20 px-3 py-2 border rounded-md bg-background text-sm"
            />
            <span className="text-sm text-muted-foreground">:</span>
            <input
              type="number"
              min={0}
              max={59}
              value={goal.paceSeconds}
              onChange={(e) => set("paceSeconds", e.target.value)}
              placeholder="sec"
              className="w-20 px-3 py-2 border rounded-md bg-background text-sm"
            />
            <select
              value={goal.paceUnit}
              onChange={(e) =>
                set("paceUnit", e.target.value as (typeof PACE_UNITS)[number])
              }
              className="px-3 py-2 border rounded-md bg-background text-sm"
            >
              {PACE_UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {goal.type === "train-event" && (
        <>
          <div>
            <FieldLabel hint="Race, competition, hike, etc.">
              Event type
            </FieldLabel>
            <input
              value={goal.eventType}
              onChange={(e) => set("eventType", e.target.value)}
              placeholder="e.g. Half marathon, Spartan, ski trip"
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Event month</FieldLabel>
              <select
                value={goal.eventMonth}
                onChange={(e) => set("eventMonth", e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              >
                <option value="">—</option>
                {MONTHS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <FieldLabel>Event year</FieldLabel>
              <input
                type="number"
                value={goal.eventYear}
                onChange={(e) => set("eventYear", e.target.value)}
                placeholder="2026"
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              />
            </div>
          </div>
          <div>
            <FieldLabel hint="Have they been training already?">
              Training status
            </FieldLabel>
            <input
              value={goal.trainingStatus}
              onChange={(e) => set("trainingStatus", e.target.value)}
              placeholder="e.g. Mid-block, just starting, returning"
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
          <div>
            <FieldLabel hint="Best recent benchmark or where they are right now">
              Current status
            </FieldLabel>
            <input
              value={goal.currentStatus}
              onChange={(e) => set("currentStatus", e.target.value)}
              placeholder="e.g. 5k in 26 min, can squat 185 lbs"
              className="w-full px-3 py-2 border rounded-md bg-background text-sm"
            />
          </div>
        </>
      )}

      {goal.type === "push-myself" && (
        <div>
          <FieldLabel hint="What kind of push are they looking for?">
            Push type
          </FieldLabel>
          <input
            value={goal.pushType}
            onChange={(e) => set("pushType", e.target.value)}
            placeholder="e.g. Mental discipline, harder cardio, longer sessions"
            className="w-full px-3 py-2 border rounded-md bg-background text-sm"
          />
        </div>
      )}

      <div>
        <FieldLabel hint='Free-text "Tell us more..." — anything the user wants the trainer to know'>
          Additional details
        </FieldLabel>
        <textarea
          value={goal.details}
          onChange={(e) => set("details", e.target.value)}
          rows={3}
          placeholder="e.g. Coming back from a hamstring strain. Wants to build glutes without aggravating it."
          className="w-full px-3 py-2 border rounded-md bg-background text-sm font-sans"
        />
      </div>
    </div>
  )
}

// Weekday names in JS Date order (Sunday=0).
const _WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const
const _WEEKDAY_FULL = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const

/** Derive the weekday from a YYYY-MM-DD ISO date string, anchored to UTC
 *  so the result is timezone-stable. (`new Date("2026-06-11")` parses as
 *  midnight UTC, then a non-UTC `getDay()` can shift the day across the
 *  date line for some users.) */
function _weekdayIndexFromIso(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return null
  const [, y, mo, d] = m
  const dt = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)))
  return Number.isNaN(dt.getTime()) ? null : dt.getUTCDay()
}

function shortWeekday(iso: string): string {
  const idx = _weekdayIndexFromIso(iso)
  return idx == null ? "" : _WEEKDAY_SHORT[idx]
}

function fullWeekday(iso: string): string {
  const idx = _weekdayIndexFromIso(iso)
  return idx == null ? "" : _WEEKDAY_FULL[idx]
}

function isWeekend(iso: string): boolean {
  const idx = _weekdayIndexFromIso(iso)
  return idx === 0 || idx === 6
}

/** The agent emits sessions with `category` like "Strength Training" /
 *  "Run" / "Yoga" — match the production output schema. The Playground
 *  form keys activityPlans by the activity NAME the admin picked
 *  ("Weightlifting", "Run", "Yoga", etc). This map bridges the two so
 *  the preferred-day check can look up the right plan entry. */
const _SESSION_CATEGORY_TO_FORM_ACTIVITY: Record<string, string> = {
  "Strength Training": "Weightlifting",
  Strength: "Weightlifting",
  Weightlifting: "Weightlifting",
  Run: "Run",
  Running: "Run",
  Swim: "Swim",
  Swimming: "Swim",
  Cycling: "Cycling",
  Bike: "Cycling",
  Yoga: "Yoga",
  Pilates: "Pilates",
  Walk: "Walk",
  Walking: "Walk",
  Hike: "Hike",
  Hiking: "Hike",
  HIIT: "HIIT",
  Rowing: "Rowing",
  Row: "Rowing",
}

/** Was the activity scheduled on a day the user said they prefer for that
 *  activity? Returns:
 *    - 'match'    → activity has explicit preferred days and the iso's
 *                   weekday is in that list (the agent honored the user's
 *                   stated availability)
 *    - 'mismatch' → activity has explicit preferred days and the iso's
 *                   weekday is NOT in that list (the agent put the
 *                   session on a day the user didn't ask for)
 *    - 'no-pref'  → user gave no explicit days for that activity
 *                   ("I have no preference" path) — no judgment possible
 *    - null       → unknown activity or no plan entry — nothing to check
 */
function preferenceCheck(
  iso: string,
  activity: string | undefined,
  activityPlans: Record<string, ActivityPlan>,
): "match" | "mismatch" | "no-pref" | null {
  if (!activity) return null
  const plan = activityPlans[activity]
  if (!plan) return null
  if (!plan.preferredDays || plan.preferredDays.length === 0) return "no-pref"
  const day = fullWeekday(iso)
  if (!day) return null
  return plan.preferredDays.includes(day) ? "match" : "mismatch"
}

/** Aggregate preference status across every session on a day. Used to color
 *  the day's status dot:
 *    - 'mismatch' (red)    → at least one session landed off the user's
 *                            preferred days for its activity
 *    - 'match' (green)     → every checkable session matched
 *    - 'no-pref' (yellow)  → nothing checkable (only no-preference
 *                            activities, or unmapped categories)
 *    - null                → no sessions at all (rest day) — render no dot
 */
function dayPreferenceStatus(
  iso: string,
  sessions: ReadonlyArray<{ category?: string; name?: string }>,
  activityPlans: Record<string, ActivityPlan>,
): "match" | "mismatch" | "no-pref" | null {
  if (sessions.length === 0) return null
  let hasMatch = false
  let hasNoPref = false
  for (const s of sessions) {
    const formActivity =
      _SESSION_CATEGORY_TO_FORM_ACTIVITY[s.category ?? ""] ??
      _SESSION_CATEGORY_TO_FORM_ACTIVITY[s.name ?? ""]
    const r = preferenceCheck(iso, formActivity, activityPlans)
    if (r === "mismatch") return "mismatch" // any single miss wins — red
    if (r === "match") hasMatch = true
    else if (r === "no-pref") hasNoPref = true
  }
  if (hasMatch) return "match"
  if (hasNoPref) return "no-pref"
  return null
}

function buildProfile(form: FormState): PlaygroundProfile {
  // Map each selected activity to its specific sessions/week + preferred-days
  // plan — exactly the shape mobile produces via the
  // RegisterSchedule + RegisterPreferredDays screens. Activities that aren't
  // currently selected are skipped.
  const trainingSchedule: Record<string, number> = {}
  const preferredDays: Record<string, string[]> = {}
  for (const a of form.activities) {
    const plan = form.activityPlans[a]
    const n = plan ? Number(plan.sessionsPerWeek) : 0
    if (n > 0) trainingSchedule[a] = n
    if (plan && plan.preferredDays.length > 0) {
      preferredDays[a] = plan.preferredDays
    }
  }

  const goals: PlaygroundGoal[] = form.goals.map((g): PlaygroundGoal => {
    const out: PlaygroundGoal = {
      type: g.type,
      name: g.name,
    }
    if (g.details.trim()) out.details = g.details.trim()
    if (g.muscleGroups.length > 0) out.muscleGroups = g.muscleGroups
    if (g.currentMuscleMass) out.currentMuscleMass = Number(g.currentMuscleMass)
    if (g.targetMuscleMass) out.targetMuscleMass = Number(g.targetMuscleMass)
    if (g.currentBodyFat) out.currentBodyFat = Number(g.currentBodyFat)
    if (g.targetBodyFat) out.targetBodyFat = Number(g.targetBodyFat)
    if (g.paceMinutes || g.paceSeconds) {
      out.targetPace = {
        minutes: Number(g.paceMinutes) || 0,
        seconds: Number(g.paceSeconds) || 0,
        unit: g.paceUnit,
      }
    }
    if (g.eventType.trim()) out.eventType = g.eventType.trim()
    if (g.eventMonth) out.eventMonth = g.eventMonth
    if (g.eventYear) out.eventYear = Number(g.eventYear)
    if (g.trainingStatus.trim()) out.trainingStatus = g.trainingStatus.trim()
    if (g.currentStatus.trim()) out.currentStatus = g.currentStatus.trim()
    if (g.pushType.trim()) out.pushType = g.pushType.trim()
    return out
  })

  // Mirror the mobile onboarding shape so dev's extract_weightlifting_maxes()
  // picks these up via _format_user_profile -> "1RM (lbs):" prompt line.
  // maxes_provided is the explicit source-of-truth flag the FIRST-WEEK
  // WEIGHT ANCHORING spec keys its 130-lb sentinel rule on — set true the
  // moment the admin enters any value (zero/blank still means false).
  const maxes: { squat?: number; bench?: number; deadlift?: number } = {}
  if (form.oneRepMaxSquat) maxes.squat = Number(form.oneRepMaxSquat)
  if (form.oneRepMaxBench) maxes.bench = Number(form.oneRepMaxBench)
  if (form.oneRepMaxDeadlift) maxes.deadlift = Number(form.oneRepMaxDeadlift)
  const maxesProvided = Object.keys(maxes).length > 0
  const onboardingData = maxesProvided
    ? { weightlifting: { maxes, maxes_provided: true } }
    : undefined

  // The case-study narrative is the admin's primary description of who to
  // generate for; the "Anything else to share?" box is supplementary. Both
  // ride into the agent on profile_summary — case study first so it leads.
  const summaryParts = [form.caseStudy.trim(), form.profileSummary.trim()].filter(
    Boolean,
  )
  const profileSummary = summaryParts.join("\n\n") || undefined

  return {
    first_name: form.firstName || undefined,
    last_name: form.lastName || undefined,
    age: form.age ? Number(form.age) : null,
    gender: form.gender || undefined,
    height_feet: form.heightFeet ? Number(form.heightFeet) : null,
    height_inches: form.heightInches ? Number(form.heightInches) : null,
    weight_lbs: form.weightLbs ? Number(form.weightLbs) : null,
    location: form.location.trim() || undefined,
    selected_activities: form.activities,
    sport_focus: form.sportFocus.length > 0 ? form.sportFocus : undefined,
    training_schedule: trainingSchedule,
    preferred_days: preferredDays,
    goals,
    equipment: form.equipment,
    onboarding_data: onboardingData,
    profile_summary: profileSummary,
  }
}

// Real-user import: map a profiles row -> FormState.
//
// The profiles row stores activity/day/goal values in the shapes mobile
// onboarding writes, which don't always match the Playground's canonical
// option lists. These maps normalize the common variants so imported values
// land on the right toggles. Unknown values are preserved verbatim — the form
// still sends them faithfully even if no toggle highlights.

/** DB selected_activities / training_schedule keys -> Playground activity. */
const _PROFILE_ACTIVITY_ALIASES: Record<string, string> = {
  Strength: "Weightlifting",
  "Strength Training": "Weightlifting",
  Weightlifting: "Weightlifting",
  Run: "Run",
  Running: "Run",
  Swim: "Swim",
  Swimming: "Swim",
  Cycling: "Cycling",
  Bike: "Cycling",
  Biking: "Cycling",
  Yoga: "Yoga",
  Pilates: "Pilates",
  Walk: "Walk",
  Walking: "Walk",
  Hike: "Hike",
  Hiking: "Hike",
  HIIT: "HIIT",
  Rowing: "Rowing",
  Row: "Rowing",
}

/** Day abbreviations / full names -> Playground full weekday name. */
const _PROFILE_DAY_ALIASES: Record<string, string> = {
  M: "Monday", Mon: "Monday", Monday: "Monday",
  Tu: "Tuesday", Tue: "Tuesday", Tues: "Tuesday", Tuesday: "Tuesday",
  W: "Wednesday", Wed: "Wednesday", Wednesday: "Wednesday",
  Th: "Thursday", Thu: "Thursday", Thurs: "Thursday", Thursday: "Thursday",
  F: "Friday", Fri: "Friday", Friday: "Friday",
  Sa: "Saturday", Sat: "Saturday", Saturday: "Saturday",
  Su: "Sunday", Sun: "Sunday", Sunday: "Sunday",
}

/** Goal `type` slugs that differ between storage and the Playground catalog.
 *  Mobile/agent store "lose-fat"; the Playground option uses "lose-body-fat". */
const _PROFILE_GOAL_TYPE_ALIASES: Record<string, string> = {
  "lose-fat": "lose-body-fat",
  "lose-body-fat": "lose-body-fat",
}

function _normalizeActivity(name: string): string {
  return _PROFILE_ACTIVITY_ALIASES[name] ?? name
}

function _normalizeDay(name: string): string {
  return _PROFILE_DAY_ALIASES[name] ?? name
}

/** Match a stored gender to the dropdown's casing.
 *
 *  `profiles.gender` is stored lowercase ("male") but GENDER_OPTIONS are
 *  title-case ("Male"), and a raw value matching no <option> makes the select
 *  silently display the FIRST one while state holds the real value. Gender
 *  drives the Mifflin BMR the nutrition engine computes, so the mismatch reads
 *  as bad data rather than a display bug.
 */
function _normalizeGender(raw: string): string {
  const v = raw.trim().toLowerCase()
  if (!v) return ""
  const hit = GENDER_OPTIONS.find((g) => g.toLowerCase() === v)
  if (hit) return hit
  // Tolerate the short forms a hand-edited row might carry.
  if (v === "m") return "Male"
  if (v === "f") return "Female"
  return raw
}

function _normalizeGoalType(slug: string): string {
  return _PROFILE_GOAL_TYPE_ALIASES[slug] ?? slug
}

function _str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v)
}

/** eventMonth may be a number (1-12) or already a month name. Normalize to a
 *  MONTHS entry so the goal editor's <select> matches. */
function _normalizeEventMonth(v: unknown): string {
  if (v === null || v === undefined || v === "") return ""
  const n = Number(v)
  if (Number.isInteger(n) && n >= 1 && n <= 12) return MONTHS[n - 1]
  const s = String(v)
  return MONTHS.includes(s as (typeof MONTHS)[number]) ? s : ""
}

function _goalDraftFromProfile(raw: Record<string, any>): GoalDraft {
  const type = _normalizeGoalType(_str(raw.type || raw.goalType))
  const meta = GOAL_PRIMARY.find((g) => g.type === type)
  const base = DEFAULT_GOAL(meta?.type ?? type, meta?.name ?? _str(raw.name) ?? type)
  return {
    ...base,
    name: _str(raw.name) || base.name,
    details: _str(raw.details),
    muscleGroups: Array.isArray(raw.muscleGroups) ? raw.muscleGroups : [],
    currentMuscleMass: _str(raw.currentMuscleMass),
    targetMuscleMass: _str(raw.targetMuscleMass),
    currentBodyFat: _str(raw.currentBodyFat),
    targetBodyFat: _str(raw.targetBodyFat),
    paceMinutes: _str(raw.targetPace?.minutes),
    paceSeconds: _str(raw.targetPace?.seconds),
    paceUnit: raw.targetPace?.unit === "min/km" ? "min/km" : "min/mile",
    eventType: _str(raw.eventType),
    eventMonth: _normalizeEventMonth(raw.eventMonth),
    eventYear: _str(raw.eventYear),
    trainingStatus: _str(raw.trainingStatus),
    currentStatus: _str(raw.currentStatus),
    pushType: _str(raw.pushType),
  }
}

/** Merge a real user's profile row into the current form. Profile-derived
 *  fields are overwritten; admin-controlled run settings (case study,
 *  system-prompt override, weeks, archetype override) are preserved. */
function formFromProfile(profile: Record<string, any>, prev: FormState): FormState {
  // Activities: normalize DB names to canonical options, dedupe.
  const rawActivities: string[] = Array.isArray(profile.selected_activities)
    ? profile.selected_activities
    : []
  const activities: string[] = []
  // Map each canonical activity back to the original DB key so we can look up
  // its schedule/preferred-days entries (which are keyed by the DB name).
  const canonicalToRaw: Record<string, string> = {}
  for (const raw of rawActivities) {
    const canon = _normalizeActivity(raw)
    if (!activities.includes(canon)) activities.push(canon)
    if (!(canon in canonicalToRaw)) canonicalToRaw[canon] = raw
  }

  const trainingSchedule: Record<string, any> =
    profile.training_schedule && typeof profile.training_schedule === "object"
      ? profile.training_schedule
      : {}
  const preferredDays: Record<string, any> =
    profile.preferred_days && typeof profile.preferred_days === "object"
      ? profile.preferred_days
      : {}

  const activityPlans: Record<string, ActivityPlan> = {}
  for (const canon of activities) {
    const rawKey = canonicalToRaw[canon] ?? canon
    // Look up by raw DB key first, then fall back to the canonical name.
    const sessions = trainingSchedule[rawKey] ?? trainingSchedule[canon]
    const days = preferredDays[rawKey] ?? preferredDays[canon]
    activityPlans[canon] = {
      sessionsPerWeek: sessions != null ? _str(sessions) : "",
      preferredDays: Array.isArray(days)
        ? days.map((d) => _normalizeDay(String(d)))
        : [],
    }
  }

  // sport_focus: tolerate either a single string or a list.
  let sportFocus: string[] = []
  if (Array.isArray(profile.sport_focus)) sportFocus = profile.sport_focus.map(String)
  else if (typeof profile.sport_focus === "string" && profile.sport_focus.trim())
    sportFocus = [profile.sport_focus.trim()]

  // Goals: at most 2. Fall back to the default goal if empty
  // so the Generate button's "add at least one goal" guard is satisfied.
  const rawGoals: any[] = Array.isArray(profile.goals) ? profile.goals : []
  const goals: GoalDraft[] = rawGoals.slice(0, 2).map(_goalDraftFromProfile)
  if (goals.length === 0) goals.push(DEFAULT_GOAL("gain-muscle", "Build muscle"))

  // Weightlifting maxes from onboarding_data.weightlifting.maxes.
  const maxes = profile.onboarding_data?.weightlifting?.maxes ?? {}

  return {
    ...prev,
    firstName: _str(profile.first_name),
    lastName: _str(profile.last_name),
    age: _str(profile.age),
    gender: _normalizeGender(_str(profile.gender)) || prev.gender,
    heightFeet: _str(profile.height_feet),
    heightInches: _str(profile.height_inches),
    weightLbs: _str(profile.weight_lbs),
    location: _str(profile.location),
    activities,
    sportFocus,
    activityPlans,
    goals,
    equipment: Array.isArray(profile.equipment) ? profile.equipment : [],
    oneRepMaxBench: _str(maxes.bench),
    oneRepMaxSquat: _str(maxes.squat),
    oneRepMaxDeadlift: _str(maxes.deadlift),
    profileSummary: _str(profile.profile_summary),
  }
}

export function PlaygroundPage() {
  const [form, setForm] = useState<FormState>(DEFAULTS)
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null)
  /** The raw imported profile row. Feeds the Nutrition tab the fields the
   *  workout form doesn't carry; null when nobody is imported. */
  const [importedProfile, setImportedProfile] = useState<Record<
    string,
    unknown
  > | null>(null)
  const [importing, setImporting] = useState(false)
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<PlaygroundResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Index of the week tab currently shown in the result panel. Resets to
   *  0 on every successful generation. */
  const [activeWeek, setActiveWeek] = useState(0)

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function setActivities(next: string[]) {
    setForm((prev) => {
      const plans = { ...prev.activityPlans }
      // Seed a default plan for any newly-selected activity so the admin can
      // start configuring it immediately, and clear plans for unselected ones.
      for (const a of next) {
        if (!plans[a]) plans[a] = { sessionsPerWeek: "", preferredDays: [] }
      }
      for (const a of Object.keys(plans)) {
        if (!next.includes(a)) delete plans[a]
      }
      return { ...prev, activities: next, activityPlans: plans }
    })
  }

  function updateActivityPlan(activity: string, next: ActivityPlan) {
    setForm((prev) => ({
      ...prev,
      activityPlans: { ...prev.activityPlans, [activity]: next },
    }))
  }

  function updateGoal(index: number, next: GoalDraft) {
    setForm((prev) => {
      const goals = prev.goals.slice()
      goals[index] = next
      return { ...prev, goals }
    })
  }

  function addGoal() {
    if (form.goals.length >= 2) {
      toast.error("Maximum 2 goals (project rule)")
      return
    }
    setForm((prev) => ({
      ...prev,
      goals: [...prev.goals, DEFAULT_GOAL("lose-body-fat", "Lose fat")],
    }))
  }

  function removeGoal(index: number) {
    setForm((prev) => ({
      ...prev,
      goals: prev.goals.filter((_, i) => i !== index),
    }))
  }

  async function handleSelectUser(user: UserOption | null) {
    setSelectedUser(user)
    if (!user) {
      setImportedProfile(null)
      return
    }
    setImporting(true)
    try {
      const profile = await getUserProfile(user.id)
      setForm((prev) => formFromProfile(profile, prev))
      // The raw row, kept alongside the mapped form so the Nutrition tab can
      // seed the fields a workout profile has no slot for (declared goal, diet,
      // allergies). Without this, importing a real member would give you their
      // BODY with default nutrition settings — a hybrid that reads like a real
      // member and isn't one.
      setImportedProfile(profile as Record<string, unknown>)
      toast.success(
        `Loaded ${user.first_name} ${user.last_name}'s profile — edit anything below before generating.`,
      )
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load profile"
      toast.error(msg)
      setSelectedUser(null)
    } finally {
      setImporting(false)
    }
  }

  async function handleRun() {
    if (form.activities.length === 0) {
      toast.error("Pick at least one activity")
      return
    }
    if (form.goals.length === 0) {
      toast.error("Add at least one goal")
      return
    }
    setRunning(true)
    setError(null)
    try {
      const profile = buildProfile(form)
      const asLevel = (v: string) =>
        v === "green" || v === "yellow" || v === "red" ? v : undefined
      const res = await runPlaygroundGeneration({
        profile,
        weeksToGenerate: form.weeksToGenerate,
        systemPrompt: form.systemPrompt,
        archetypeOverride: form.archetypeOverride,
        wearable: form.wearableEnabled
          ? {
              enabled: true,
              hrv_level: asLevel(form.hrvLevel),
              rhr_level: asLevel(form.rhrLevel),
              sleep_level: asLevel(form.sleepLevel),
              subjective_level: asLevel(form.subjectiveLevel),
            }
          : undefined,
      })
      setResult(res)
      setActiveWeek(0)
      toast.success("Generated! Scroll down to see the plan.")
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Generation failed"
      setError(msg)
      toast.error(msg)
    } finally {
      setRunning(false)
    }
  }

  // The profile builder, held in a variable so BOTH tabs render the IDENTICAL
  // form bound to the IDENTICAL state. A nutrition tab with its own copy would
  // drift from the exercise tab the first time either one changed, and the whole
  // point of the shared builder is that a member described once is the same
  // member on both sides. Switching tabs preserves everything, because the
  // state lives here on the page rather than inside the form.
  const profileSections = (
    <>
          <Section
            icon={FileText}
            title="User profile / case study"
            description="Write out who this user is, like a case study — their background, history, constraints, what they're after. This narrative goes to the agent as primary context (front of profile_summary), so the model generates for the person you describe. Works on its own or layered on top of an imported user."
          >
            <textarea
              value={form.caseStudy}
              onChange={(e) => setField("caseStudy", e.target.value)}
              rows={8}
              placeholder={
                "e.g. 38-year-old former college soccer player, 3 kids, hasn't trained consistently in 6 years. Carrying ~25 lbs more than playing weight. Wants to feel athletic again and drop body fat without wrecking his knees (old ACL reconstruction, left). Can train 4x/week, early mornings, home garage gym with a barbell, rack, and dumbbells. Hates long steady-state cardio; loves competition and measurable progress."
              }
              className="w-full px-3 py-2 border rounded-md bg-background text-sm font-sans"
            />
          </Section>

          <Section
            icon={User}
            title="About the user"
            description="Name, age, body stats and where they live."
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel>First name</FieldLabel>
                <input
                  value={form.firstName}
                  onChange={(e) => setField("firstName", e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
              <div>
                <FieldLabel>Last name</FieldLabel>
                <input
                  value={form.lastName}
                  onChange={(e) => setField("lastName", e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <FieldLabel>Age</FieldLabel>
                <input
                  type="number"
                  min={13}
                  max={99}
                  value={form.age}
                  onChange={(e) => setField("age", e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
              <div>
                <FieldLabel>Gender</FieldLabel>
                <select
                  value={form.gender}
                  onChange={(e) => setField("gender", e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                >
                  {GENDER_OPTIONS.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>Weight (lbs)</FieldLabel>
                <input
                  type="number"
                  min={60}
                  max={500}
                  value={form.weightLbs}
                  onChange={(e) => setField("weightLbs", e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 items-end">
              <div>
                <FieldLabel>Height</FieldLabel>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={3}
                    max={8}
                    value={form.heightFeet}
                    onChange={(e) => setField("heightFeet", e.target.value)}
                    className="w-20 px-3 py-2 border rounded-md bg-background text-sm"
                  />
                  <span className="text-sm text-muted-foreground">ft</span>
                  <input
                    type="number"
                    min={0}
                    max={11}
                    value={form.heightInches}
                    onChange={(e) => setField("heightInches", e.target.value)}
                    className="w-20 px-3 py-2 border rounded-md bg-background text-sm"
                  />
                  <span className="text-sm text-muted-foreground">in</span>
                </div>
              </div>
              <div>
                <FieldLabel hint="City, state — used for scheduling context">
                  Location
                </FieldLabel>
                <input
                  value={form.location}
                  onChange={(e) => setField("location", e.target.value)}
                  placeholder="e.g. San Francisco, CA"
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
            </div>
          </Section>

          <Section
            icon={CalendarRange}
            title="Training availability"
            description="Pick activities, then set the sessions per week and preferred days for each one — exactly like mobile onboarding."
          >
            <div>
              <FieldLabel hint="What kinds of workouts do they want? Pick one or more.">
                Activities
              </FieldLabel>
              <Toggle
                options={ACTIVITY_OPTIONS}
                selected={form.activities}
                onChange={(next) => setActivities(next)}
                ariaLabel="Activities"
              />
            </div>

            {form.activities.length === 0 ? (
              <div className="rounded-md border border-dashed bg-muted/30 p-4 text-center text-xs text-muted-foreground">
                Pick at least one activity above to set its schedule.
              </div>
            ) : (
              <div className="space-y-3">
                {form.activities.map((activity) => {
                  const plan: ActivityPlan = form.activityPlans[activity] ?? {
                    sessionsPerWeek: "",
                    preferredDays: [],
                  }
                  return (
                    <div
                      key={activity}
                      className="rounded-md border bg-background p-3 space-y-3"
                    >
                      <div className="flex items-baseline justify-between">
                        <span className="text-sm font-semibold">{activity}</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          per-activity schedule
                        </span>
                      </div>
                      <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
                        <div>
                          <FieldLabel hint="per week">Sessions</FieldLabel>
                          <input
                            type="number"
                            min={0}
                            max={7}
                            value={plan.sessionsPerWeek}
                            onChange={(e) => updateActivityPlan(activity, {
                              ...plan,
                              sessionsPerWeek: e.target.value,
                            })}
                            placeholder="0"
                            className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                          />
                        </div>
                        <div>
                          <FieldLabel hint="Days they prefer for this activity">
                            Preferred days
                          </FieldLabel>
                          <Toggle
                            options={DAY_OPTIONS}
                            selected={plan.preferredDays}
                            onChange={(next) => updateActivityPlan(activity, {
                              ...plan,
                              preferredDays: next,
                            })}
                            ariaLabel={`${activity} preferred days`}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Section>

          <Section
            icon={Target}
            title="Sport focus (optional)"
            description="If the user trains for a specific sport, pick it here. Any non-empty selection triggers the Sport-Specific archetype — Phase 4 (Contralateral/Stability) becomes the 'Golden Phase' and T.R.A.C. C-block gets plyometric emphasis."
          >
            <div>
              <FieldLabel hint="Multi-select. Leave empty if the user isn't training for a sport.">
                Sports
              </FieldLabel>
              <Toggle
                options={SPORT_OPTIONS}
                selected={form.sportFocus}
                onChange={(next) => setField("sportFocus", next)}
                ariaLabel="Sport focus"
              />
            </div>
          </Section>

          <Section
            icon={Dumbbell}
            title="Equipment & access"
            description="What's available to them when they train."
          >
            <Toggle
              options={EQUIPMENT_OPTIONS}
              selected={form.equipment}
              onChange={(next) => setField("equipment", next)}
              ariaLabel="Equipment"
            />
          </Section>

          <Section
            icon={Weight}
            title="Strength benchmarks"
            description="Current 1-rep max for the big lifts (optional, lbs). Leave blank if unknown."
          >
            <div className="grid grid-cols-3 gap-3">
              <div>
                <FieldLabel hint="lbs">Bench press 1RM</FieldLabel>
                <input
                  type="number"
                  min={0}
                  value={form.oneRepMaxBench}
                  onChange={(e) => setField("oneRepMaxBench", e.target.value)}
                  placeholder="e.g. 185"
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
              <div>
                <FieldLabel hint="lbs">Squat 1RM</FieldLabel>
                <input
                  type="number"
                  min={0}
                  value={form.oneRepMaxSquat}
                  onChange={(e) => setField("oneRepMaxSquat", e.target.value)}
                  placeholder="e.g. 245"
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
              <div>
                <FieldLabel hint="lbs">Deadlift 1RM</FieldLabel>
                <input
                  type="number"
                  min={0}
                  value={form.oneRepMaxDeadlift}
                  onChange={(e) => setField("oneRepMaxDeadlift", e.target.value)}
                  placeholder="e.g. 315"
                  className="w-full px-3 py-2 border rounded-md bg-background text-sm"
                />
              </div>
            </div>
          </Section>

          <Section
            icon={Target}
            title="Goals"
            description="Up to 2 — pick a goal and add specifics."
          >
            {form.goals.map((g, i) => (
              <GoalEditor
                key={i}
                goal={g}
                index={i}
                onUpdate={(next) => updateGoal(i, next)}
                onRemove={() => removeGoal(i)}
                canRemove={form.goals.length > 1}
              />
            ))}
            {form.goals.length < 2 && (
              <button
                type="button"
                onClick={addGoal}
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <Plus className="size-3.5" /> Add a second goal
              </button>
            )}
          </Section>

          <Section
            icon={NotebookPen}
            title="Anything else to share?"
            description="Free-text. Goes straight to the agent as profile_summary."
          >
            <textarea
              value={form.profileSummary}
              onChange={(e) => setField("profileSummary", e.target.value)}
              rows={4}
              placeholder="e.g. Returning lifter coming back from a knee injury. Lives in a small apartment, no barbell. Loves morning workouts."
              className="w-full px-3 py-2 border rounded-md bg-background text-sm font-sans"
            />
          </Section>

          <Section
            icon={HeartPulse}
            title="Wearable / readiness (optional)"
            description="Simulate a connected wearable. Set HRV, resting HR, and sleep to a Green/Yellow/Red level; the backend maps each to representative values and runs the SAME readiness scorer real users hit (HRV 40% · RHR 30% · Sleep 30%, with the anti-outlier gates). The resulting state flows into generation — YELLOW eases volume/load, RED becomes active recovery. Leave off to generate as a no-wearable user (defaults to GREEN)."
          >
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.wearableEnabled}
                onChange={(e) => setField("wearableEnabled", e.target.checked)}
              />
              <span className="font-medium">Simulate a wearable connection</span>
            </label>
            {form.wearableEnabled && (
              <div className="space-y-3 rounded-md border bg-background p-3">
                <LevelRow
                  label="HRV"
                  hint="Heart-rate variability vs baseline (higher = better)"
                  value={form.hrvLevel}
                  onChange={(v) => setField("hrvLevel", v)}
                />
                <LevelRow
                  label="Resting HR"
                  hint="Resting heart rate vs baseline (lower = better)"
                  value={form.rhrLevel}
                  onChange={(v) => setField("rhrLevel", v)}
                />
                <LevelRow
                  label="Sleep"
                  hint="Sleep duration / quality"
                  value={form.sleepLevel}
                  onChange={(v) => setField("sleepLevel", v)}
                />
                <LevelRow
                  label="Daily check-in"
                  hint="Optional subjective energy/mood/body (3-question check-in)"
                  value={form.subjectiveLevel}
                  onChange={(v) => setField("subjectiveLevel", v)}
                />
                <p className="text-xs text-muted-foreground">
                  Tip: a single metric set to Red is treated as low-confidence and
                  capped at Yellow by the real anti-outlier gate — set two or more
                  to drive a true Red. The computed state appears in the output.
                </p>
              </div>
            )}
          </Section>

          <Section
            icon={NotebookPen}
            title="System prompt override (advanced)"
            description="Paste a complete system prompt to A/B test it. When non-empty, REPLACES the entire default system prompt — the principles, exercise library, and weight-anchoring blocks are all bypassed. Leave empty to use the default. The default lives in backend/api/routers/admin.py _build_test_gen_system_prompt()."
          >
            <textarea
              value={form.systemPrompt}
              onChange={(e) => setField("systemPrompt", e.target.value)}
              rows={12}
              placeholder="Leave empty to use the default test-generation system prompt. Paste your candidate prompt here to test it instead."
              className="w-full px-3 py-2 border rounded-md bg-background text-xs font-mono"
            />
            {form.systemPrompt.trim() && (
              <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                Override active — default system prompt bypassed.
              </p>
            )}
          </Section>
    </>
  )

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <FlaskConical className="size-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold">Generation Playground</h1>
          <p className="text-xs text-muted-foreground max-w-2xl">
            Every field a real user would fill in the mobile onboarding flow is here. Try different
            scenarios; nothing is saved. Use this to validate prompt changes and check the agent's
            output before shipping.
          </p>
        </div>
      </div>

      {/* Import a real user — search any onboarded user and load their
          preferences, goals, and onboarding selections into the form below. */}
      <div className="rounded-lg border bg-card p-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-muted p-2">
              <Users className="size-4 text-muted-foreground" />
            </div>
            <div>
              <h2 className="text-sm font-semibold leading-tight">
                Import a real user
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                Search any onboarded user to load their preferences, goals, and
                onboarding selections into the form below. Everything stays
                editable — nothing is saved back to their account.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <UserCombobox
              value={selectedUser}
              onSelect={handleSelectUser}
              placeholder="Search onboarded users..."
            />
            {importing && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="size-3.5 animate-spin" /> Loading profile...
              </span>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="exercise">
        <TabsList>
          <TabsTrigger value="exercise">Exercise</TabsTrigger>
          <TabsTrigger value="nutrition">Nutrition</TabsTrigger>
        </TabsList>

        <TabsContent value="exercise" className="mt-4">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
        {/* Form */}
        <div className="space-y-5">
          {profileSections}

          <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-lg border bg-card p-3 shadow-lg">
            <button
              type="button"
              onClick={() => {
                setForm(DEFAULTS)
                setSelectedUser(null)
                setImportedProfile(null)
              }}
              className="text-xs text-muted-foreground hover:text-foreground underline"
            >
              Reset to defaults
            </button>
            <div className="flex items-center gap-3">
              <label
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
                title="Force a specific archetype. Leave on Auto to let the rule-based classifier decide from sport_focus / goals / activities."
              >
                <span>Archetype</span>
                <select
                  value={form.archetypeOverride}
                  onChange={(e) => setField("archetypeOverride", e.target.value)}
                  disabled={running}
                  className="px-2 py-1 border rounded-md bg-background text-xs"
                >
                  <option value="">Auto</option>
                  {Object.entries(ARCHETYPE_LABEL).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span>Weeks</span>
                <select
                  value={form.weeksToGenerate}
                  onChange={(e) => setField("weeksToGenerate", Number(e.target.value))}
                  disabled={running}
                  className="px-2 py-1 border rounded-md bg-background text-xs"
                  title="How many consecutive weeks to generate. Higher counts take longer and burn more tokens."
                >
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={handleRun}
                disabled={running}
                className="inline-flex items-center gap-2 rounded-md bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-60"
              >
                {running ? (
                  <>
                    <Loader2 className="size-4 animate-spin" /> Generating...
                  </>
                ) : (
                  <>
                    <Play className="size-4" /> Generate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {/* Results */}
        <div className="space-y-4 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto">
          <div className="flex items-center gap-2">
            <Ruler className="size-4 text-muted-foreground" />
            <h2 className="text-lg font-semibold">AI Output</h2>
          </div>

          {!result && !error && !running && (
            <div className="rounded-lg border border-dashed bg-card p-12 text-center text-sm text-muted-foreground">
              Fill the form and click Generate. The agent's response and the
              proposed workout week will appear here.
            </div>
          )}

          {running && (
            <div className="rounded-lg border bg-card p-12 text-center">
              <Loader2 className="size-8 animate-spin mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                Asking the agent... this usually takes 20-60 seconds.
              </p>
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 flex items-start gap-3">
              <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-destructive">
                  Generation failed
                </p>
                <p className="text-xs text-destructive/80 mt-1">{error}</p>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="rounded-md border bg-amber-50 border-amber-200 p-3 text-xs text-amber-800">
                {result.note}
              </div>

              <div className="rounded-lg border bg-card p-4 space-y-3">
                <h3 className="font-semibold text-sm">Trainer's note</h3>
                <p className="text-sm whitespace-pre-wrap text-foreground/90">
                  {result.response || "(no response text)"}
                </p>
              </div>

              <div className="rounded-lg border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-sm">Proposed plan</h3>
                  <span className="text-xs text-muted-foreground">
                    {result.season.cycles?.length ?? 0} cycle ·{' '}
                    {result.season.cycles?.[0]?.weeks?.length ?? 0} week
                  </span>
                </div>
                <div className="text-xs space-y-1.5">
                  {/* User goals — what the synthetic profile was trying to achieve.
                      Replaces the AI-emitted season.goal (which is always something
                      generic like "Helix Training Season"). */}
                  <div>
                    <span className="text-muted-foreground">User goals:</span>{' '}
                    <span className="font-medium">
                      {form.goals.length > 0
                        ? form.goals.map((g) => g.name).join(", ")
                        : "—"}
                    </span>
                  </div>
                  {/* Archetype: the deterministic classifier's pick. If the admin
                      forced an override via the Archetype dropdown, we also surface
                      what the classifier *would* have said for diff visibility. */}
                  {result.effective_archetype && (
                    <div>
                      <span className="text-muted-foreground">Archetype:</span>{' '}
                      <span className="font-medium">
                        {labelForArchetype(result.effective_archetype)}
                      </span>
                      {result.computed_archetype &&
                        result.computed_archetype !== result.effective_archetype && (
                          <span className="ml-2 text-amber-700">
                            (override — classifier would pick{' '}
                            {labelForArchetype(result.computed_archetype)})
                          </span>
                        )}
                    </div>
                  )}
                  {/* Readiness: the REAL scorer's verdict on the synthetic
                      wearable inputs. Confirms the wearable signal that actually
                      reached generation (and why, via the cause string). */}
                  {result.computed_readiness && (
                    <div>
                      <span className="text-muted-foreground">Readiness:</span>{' '}
                      <span
                        className={`font-semibold ${
                          result.computed_readiness.state === "GREEN"
                            ? "text-emerald-600"
                            : result.computed_readiness.state === "YELLOW"
                            ? "text-amber-600"
                            : "text-red-600"
                        }`}
                      >
                        {result.computed_readiness.state}
                      </span>
                      <span className="ml-2 text-muted-foreground">
                        ({result.computed_readiness.cause})
                      </span>
                    </div>
                  )}
                  {result.season.priorities?.length > 0 && (
                    <div>
                      <span className="text-muted-foreground">Priorities:</span>{' '}
                      {result.season.priorities.join(", ")}
                    </div>
                  )}
                  {result.validation_warnings && result.validation_warnings.length > 0 && (
                    <div className="text-amber-700">
                      <span className="text-muted-foreground">Warnings:</span>{' '}
                      {result.validation_warnings.join("; ")}
                    </div>
                  )}
                </div>

                {(() => {
                  const allWeeks = walkSeasonWeeks(result.season)
                  const safeActive = Math.min(activeWeek, Math.max(allWeeks.length - 1, 0))
                  const visibleWeeks =
                    allWeeks.length > 1 ? [allWeeks[safeActive]] : allWeeks
                  return (
                    <>
                      {allWeeks.length > 1 && (
                        <div
                          className="flex flex-wrap gap-1.5 pt-2 border-t"
                          role="tablist"
                          aria-label="Week selector"
                        >
                          {allWeeks.map((_, i) => {
                            const on = i === safeActive
                            return (
                              <button
                                key={i}
                                type="button"
                                role="tab"
                                aria-selected={on}
                                onClick={() => setActiveWeek(i)}
                                className={`px-3 py-1 rounded-full border text-xs transition-colors ${
                                  on
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-background hover:bg-accent"
                                }`}
                              >
                                Week {i + 1}
                              </button>
                            )
                          })}
                        </div>
                      )}
                      <div className={`space-y-3 ${allWeeks.length > 1 ? "" : "pt-2 border-t"}`}>
                        {visibleWeeks.map(({ week, weekIndex }) => {
                          // Earliest ISO key in this week — used as the week's
                          // anchor date for the week-scope feedback row.
                          const weekDates = Object.keys(week.days || {}).sort()
                          const weekStartIso = weekDates[0] ?? null
                          return (
                    <div key={weekIndex} className="space-y-2">
                      {week.goal && (
                        <p className="text-xs text-muted-foreground italic">
                          Week {weekIndex + 1} goal: {week.goal}
                        </p>
                      )}
                      {/* Admin validation: rate the whole week's structure.
                          Renders above the day list so the admin reviews the
                          arc first, then drills into individual days. */}
                      {weekStartIso && (
                        <PlaygroundWeekFeedback
                          weekStart={weekStartIso}
                          weekSnapshot={week as unknown as Record<string, unknown>}
                          profileSnapshot={result.profile_used}
                        />
                      )}
                      <div className="space-y-2">
                        {Object.entries(week.days || {}).map(([iso, rawDay]) => {
                          const day = rawDay as DayData
                          const label = getDayLabel(day)
                          const dow = shortWeekday(iso)
                          const weekend = isWeekend(iso)
                          // Tailwind: weekend days get a subtle blue tint to
                          // visually separate weekday vs weekend distribution.
                          const dateCellCls = weekend
                            ? "text-xs font-mono text-blue-600 dark:text-blue-400 font-semibold"
                            : "text-xs font-mono text-muted-foreground"
                          // Per-day aggregate preferred-day status. Rendered
                          // as a single colored dot next to the date so the
                          // signal is visible without taking up row space.
                          const sessionsForStatus = isRestDay(day)
                            ? []
                            : getDaySessions(day)
                          const prefStatus = dayPreferenceStatus(
                            iso,
                            sessionsForStatus,
                            form.activityPlans,
                          )
                          const prefDotCls =
                            prefStatus === "match"
                              ? "bg-emerald-500"
                              : prefStatus === "no-pref"
                              ? "bg-amber-400"
                              : prefStatus === "mismatch"
                              ? "bg-red-500"
                              : "" // null → no dot
                          const prefDotTitle =
                            prefStatus === "match"
                              ? "All sessions landed on user-preferred days"
                              : prefStatus === "no-pref"
                              ? "User gave no preferred-day data for these activities"
                              : prefStatus === "mismatch"
                              ? "At least one session landed off the user's preferred days"
                              : undefined
                          const dateBlock = (
                            <span className="flex items-baseline gap-1.5 w-32 shrink-0">
                              {prefDotCls && (
                                <span
                                  className={`inline-block w-2 h-2 rounded-full ${prefDotCls}`}
                                  title={prefDotTitle}
                                  aria-label={prefDotTitle}
                                />
                              )}
                              <span className={dateCellCls}>
                                {dow} {iso}
                              </span>
                            </span>
                          )
                          if (isRestDay(day)) {
                            return (
                              <div
                                key={iso}
                                className="rounded border bg-muted/30 overflow-hidden"
                              >
                                <div className="flex items-baseline gap-3 px-3 py-2">
                                  {dateBlock}
                                  <span className="text-xs font-medium w-24 shrink-0">
                                    {label}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    Rest — recovery day
                                  </span>
                                </div>
                                {/* Admin can flag misplaced rest days (e.g. rest the
                                    day before a race, or rest after a light day). */}
                                <PlaygroundDayFeedback
                                  workoutDate={iso}
                                  daySnapshot={day as unknown as Record<string, unknown>}
                                  profileSnapshot={result.profile_used}
                                />
                              </div>
                            )
                          }
                          const sessions = getDaySessions(day)
                          return (
                            <div
                              key={iso}
                              className="rounded border bg-card overflow-hidden"
                            >
                              <div className="flex items-baseline gap-3 px-3 py-2 bg-muted/40 border-b">
                                {dateBlock}
                                <span className="text-xs font-semibold">
                                  {label}
                                </span>
                              </div>
                              <div className="divide-y">
                                {sessions.map((session, sIdx) => {
                                  const exercises = session.exercises ?? []
                                  const sessionLabel =
                                    session.name || session.category || "Workout"
                                  // Per-session dot only on multi-session
                                  // days where the day-level aggregate is red.
                                  // Single-session days and all-green days
                                  // don't need per-session detail — the day
                                  // dot already carries the signal.
                                  let perSessionDot: React.ReactNode = null
                                  if (sessions.length >= 2 && prefStatus === "mismatch") {
                                    const formActivity =
                                      _SESSION_CATEGORY_TO_FORM_ACTIVITY[
                                        session.category ?? ""
                                      ] ??
                                      _SESSION_CATEGORY_TO_FORM_ACTIVITY[
                                        session.name ?? ""
                                      ]
                                    const pref = preferenceCheck(
                                      iso,
                                      formActivity,
                                      form.activityPlans,
                                    )
                                    const cls =
                                      pref === "match"
                                        ? "bg-emerald-500"
                                        : pref === "mismatch"
                                        ? "bg-red-500"
                                        : pref === "no-pref"
                                        ? "bg-amber-400"
                                        : ""
                                    const titleText =
                                      pref === "match"
                                        ? "This session landed on a user-preferred day"
                                        : pref === "mismatch"
                                        ? "This session landed OFF the user's preferred days"
                                        : pref === "no-pref"
                                        ? "No preferred-day data for this activity"
                                        : undefined
                                    if (cls) {
                                      perSessionDot = (
                                        <span
                                          className={`inline-block w-2 h-2 rounded-full shrink-0 ${cls}`}
                                          title={titleText}
                                          aria-label={titleText}
                                        />
                                      )
                                    }
                                  }
                                  return (
                                    <div key={sIdx} className="px-3 py-2 space-y-1.5">
                                      <div className="flex items-baseline justify-between gap-2">
                                        <span className="text-xs font-medium flex items-baseline gap-1.5">
                                          {perSessionDot}
                                          {sessionLabel}
                                        </span>
                                        {session.goal && (
                                          <span className="text-[10px] text-muted-foreground italic truncate">
                                            {session.goal}
                                          </span>
                                        )}
                                      </div>
                                      {exercises.length === 0 ? (
                                        <p className="text-[11px] text-muted-foreground italic">
                                          (no exercises)
                                        </p>
                                      ) : (
                                        <table className="w-full text-[11px]">
                                          <thead className="text-muted-foreground">
                                            <tr>
                                              <th className="text-left font-normal pr-2">Exercise</th>
                                              <th className="text-right font-normal w-10">Sets</th>
                                              <th className="text-right font-normal w-16">Reps</th>
                                              <th className="text-right font-normal w-20">Weight</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {exercises.map((ex, eIdx) => (
                                              <tr key={eIdx} className="align-baseline">
                                                <td className="pr-2 py-0.5">{ex.exercise}</td>
                                                <td className="text-right tabular-nums py-0.5">{ex.sets}</td>
                                                <td className="text-right tabular-nums py-0.5">{ex.reps}</td>
                                                <td className="text-right tabular-nums py-0.5">
                                                  {ex.weight > 0 ? `${ex.weight} lb` : "BW"}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                              {/* Admin validation: thumbs+notes on this playground-generated
                                  day. Submits to /admin/validation/feedback with scope='playground'
                                  and a frozen snapshot of the day + synthetic profile so the
                                  rating remains a usable post-training sample. */}
                              <PlaygroundDayFeedback
                                workoutDate={iso}
                                daySnapshot={day as unknown as Record<string, unknown>}
                                profileSnapshot={result.profile_used}
                              />
                            </div>
                          )
                        })}
                      </div>
                    </div>
                          )
                        })}
                      </div>
                    </>
                  )
                })()}

                <details className="pt-2 border-t">
                  <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                    View raw JSON (advanced)
                  </summary>
                  <pre className="mt-2 bg-foreground text-background rounded-md p-3 text-[10px] overflow-auto max-h-96">
                    {JSON.stringify(result.season, null, 2)}
                  </pre>
                </details>
              </div>

              <details className="rounded-lg border bg-card p-4">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                  Profile sent to the agent
                </summary>
                <pre className="mt-2 text-[10px] whitespace-pre-wrap bg-muted/40 rounded-md p-3 max-h-72 overflow-auto">
                  {JSON.stringify(result.profile_used, null, 2)}
                </pre>
              </details>
            </div>
          )}
        </div>
        </div>
        </TabsContent>

        <TabsContent value="nutrition" className="mt-4">
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-6">
        {/* The same builder as the Exercise tab, bound to the same state. */}
        <div className="space-y-5">{profileSections}</div>

        <NutritionCoachPanel
          profile={buildProfile(form)}
          importedProfile={importedProfile}
        />
        </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
